import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { profileApi } from '@/api/endpoints'
import { ChipsInput, ChoiceChips } from '@/components/ui/Chips'
import { TextField } from '@/components/ui/Field'
import { Switch } from '@/components/ui/Toggle'
import { toast } from '@/store/toast'
import type { JobPreference, Profile } from '@/types/api'

import { FORM_SPAN, FieldGroup, SaveFooter, SectionCard, useSectionForm } from './shared'

const EMPLOYMENT_OPTIONS = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
]

const WORKPLACE_OPTIONS = [
  { value: 'remote', label: 'Remote' },
  { value: 'onsite', label: 'On-site' },
  { value: 'hybrid', label: 'Hybrid' },
]

/** Every field of the preference, as the form holds it (numbers as text). */
interface Draft {
  desiredTitles: string[]
  desiredLocations: string[]
  excludedKeywords: string[]
  excludedCompanies: string[]
  targetCompanies: string[]
  industries: string[]
  workplaceTypes: string[]
  employmentTypes: string[]
  minSalary: string
  maxSalary: string
  salaryCurrency: string
  minExperienceYears: number | null
  maxExperienceYears: number | null
  requiresVisaSponsorship: boolean
  openToRemoteWorldwide: boolean
  matchLimit: number | null
}

const draftFrom = (profile: Profile): Draft => {
  const p = profile.jobPreference
  return {
    desiredTitles: p?.desiredTitles ?? [],
    desiredLocations: p?.desiredLocations ?? [],
    excludedKeywords: p?.excludedKeywords ?? [],
    excludedCompanies: p?.excludedCompanies ?? [],
    targetCompanies: p?.targetCompanies ?? [],
    industries: p?.industries ?? [],
    workplaceTypes: p?.workplaceTypes ?? [],
    employmentTypes: p?.employmentTypes ?? [],
    minSalary: p?.minSalary?.toString() ?? '',
    maxSalary: p?.maxSalary?.toString() ?? '',
    salaryCurrency: p?.salaryCurrency ?? profile.salaryCurrency ?? 'INR',
    minExperienceYears: p?.minExperienceYears ?? null,
    maxExperienceYears: p?.maxExperienceYears ?? null,
    requiresVisaSponsorship: p?.requiresVisaSponsorship ?? false,
    openToRemoteWorldwide: p?.openToRemoteWorldwide ?? false,
    matchLimit: p?.matchLimit ?? 50,
  }
}

function toPayload(draft: Draft): JobPreference {
  const number = (text: string) => (text.trim() === '' ? null : Number(text))
  return {
    desiredTitles: draft.desiredTitles,
    desiredLocations: draft.desiredLocations,
    excludedKeywords: draft.excludedKeywords,
    excludedCompanies: draft.excludedCompanies,
    targetCompanies: draft.targetCompanies,
    industries: draft.industries,
    workplaceTypes: draft.workplaceTypes,
    employmentTypes: draft.employmentTypes,
    minSalary: number(draft.minSalary),
    maxSalary: number(draft.maxSalary),
    salaryCurrency: draft.salaryCurrency.trim().toUpperCase() || 'INR',
    minExperienceYears: draft.minExperienceYears,
    maxExperienceYears: draft.maxExperienceYears,
    requiresVisaSponsorship: draft.requiresVisaSponsorship,
    openToRemoteWorldwide: draft.openToRemoteWorldwide,
    matchLimit: draft.matchLimit ?? 50,
  }
}

/**
 * One form, one Save: the preference is PUT whole, so the groups below are
 * panels inside a single section rather than cards with their own buttons.
 */
export function PreferencesTab({ profile }: { profile: Profile }) {
  const queryClient = useQueryClient()
  const { form, update, dirty, markSaved, savedAt } = useSectionForm(profile, draftFrom)

  const save = useMutation({
    mutationFn: () => {
      const payload = toPayload(form)
      if (payload.minSalary != null && payload.maxSalary != null && payload.minSalary > payload.maxSalary) {
        throw new ApiError('Minimum salary is above the maximum', 422, 'validation_error', null)
      }
      // PUT replaces the whole preference, so every field goes up together.
      return profileApi.updatePreferences(payload)
    },
    onSuccess: () => {
      markSaved()
      for (const key of ['profile', 'profile-recommendations', 'career-paths', 'top-matches', 'recommendations']) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
      toast.success('Saved')
    },
    onError: (error: ApiError) => toast.error('Could not save', error.message),
  })

  return (
    <SectionCard
      id="profile-preferences"
      title="Job preferences"
      description="The roles you want, where, and what they should pay. Used to rank jobs and to answer salary questions."
      footer={
        <SaveFooter
          onSave={() => save.mutate()}
          saving={save.isPending}
          dirty={dirty}
          savedAt={savedAt}
          hint="Re-ranks your job feed."
        />
      }
    >
      <div className="space-y-3">
        <FieldGroup
          id="profile-pref-compensation"
          title="Compensation and employment"
          description="The roles you want and what they should pay."
        >
          <ChipsInput
            label="Preferred job titles"
            values={form.desiredTitles}
            onChange={(values) => update('desiredTitles', values)}
            placeholder="e.g. Engineering Manager"
            hint="Press Enter or a comma after each one. Seniority words are optional."
            className={FORM_SPAN}
          />
          <div className="grid grid-cols-2 gap-3 md:col-span-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_120px] md:gap-4">
            <TextField
              label="Preferred salary (min)"
              appearance="box"
              type="number"
              min={0}
              value={form.minSalary}
              onChange={(event) => update('minSalary', event.target.value)}
              hint="Yearly."
              className="num"
            />
            <TextField
              label="Preferred salary (max)"
              appearance="box"
              type="number"
              min={0}
              value={form.maxSalary}
              onChange={(event) => update('maxSalary', event.target.value)}
              className="num"
            />
            <div className="col-span-2 md:col-span-1">
              <TextField
                label="Currency"
                appearance="box"
                maxLength={3}
                placeholder="INR"
                value={form.salaryCurrency}
                onChange={(event) => update('salaryCurrency', event.target.value.toUpperCase())}
                className="num uppercase"
              />
            </div>
          </div>
          <ChoiceChips
            label="Preferred employment type"
            options={EMPLOYMENT_OPTIONS}
            values={form.employmentTypes}
            onChange={(values) => update('employmentTypes', values)}
            hint="None selected means any."
            className={FORM_SPAN}
          />
        </FieldGroup>

        <FieldGroup
          id="profile-pref-location"
          title="Work setting and location"
          description="Where you want to work, and how."
        >
          <ChoiceChips
            label="Work setting"
            options={WORKPLACE_OPTIONS}
            values={form.workplaceTypes}
            onChange={(values) => update('workplaceTypes', values)}
            hint="None selected means any."
            className={FORM_SPAN}
          />
          <ChipsInput
            label="Preferred locations"
            values={form.desiredLocations}
            onChange={(values) => update('desiredLocations', values)}
            placeholder={profile.location?.city ? `e.g. ${profile.location.city}` : 'e.g. Bengaluru'}
            hint="Leave empty to search the whole country you can work in; jobs near you rank higher."
            className={FORM_SPAN}
          />
          <div className="flex min-h-[44px] items-center justify-between gap-4 rounded-field border border-line px-4 py-3 md:col-span-2">
            <div className="min-w-0">
              <p className="text-[14.5px] font-medium text-ink">Open to remote worldwide</p>
              <p className="text-[13px] text-ink-muted">Include remote roles based in other countries.</p>
            </div>
            <Switch
              checked={form.openToRemoteWorldwide}
              onChange={(checked) => update('openToRemoteWorldwide', checked)}
              label="Open to remote worldwide"
            />
          </div>
        </FieldGroup>

        <FieldGroup id="profile-pref-company" title="Company" description="Employers to aim for, and ones to leave out.">
          <ChipsInput
            label="Target companies"
            values={form.targetCompanies}
            onChange={(values) => update('targetCompanies', values)}
            placeholder="e.g. Stripe"
            hint="Their openings rank higher."
          />
          <ChipsInput
            label="Excluded companies"
            values={form.excludedCompanies}
            onChange={(values) => update('excludedCompanies', values)}
            placeholder="e.g. your current employer"
            hint="Never shown and never applied to."
          />
          <ChipsInput
            label="Excluded keywords"
            values={form.excludedKeywords}
            onChange={(values) => update('excludedKeywords', values)}
            placeholder="e.g. unpaid, night shift"
            hint="Jobs whose title or description contain these are left out."
            className={FORM_SPAN}
          />
        </FieldGroup>
      </div>
    </SectionCard>
  )
}
